import React, { useState } from "react";
import type {
  ChangeEventHandler,
  KeyboardEventHandler,
  ReactElement,
} from "react";
import FieldError from "./FieldError";
import Label from "./Label";
import className from "~/kernel/styling/className";
import IconButton from "./IconButton";
import isPresent from "~/kernel/validation/isPresent";
import Modal, { ModalFooter } from "./Modal";
import Button from "./Button";

type TextAreaProps = {
  label?: string | ReactElement;
  id: string;
  name?: string;
  defaultValue?: string;
  value?: string;
  required?: boolean;
  autocomplete?: "off" | "name" | "email" | "new-password";
  error?: string;
  tabIndex?: number;
  onChange?: ChangeEventHandler<HTMLTextAreaElement>;
  onBlur?: ChangeEventHandler<HTMLTextAreaElement>;
  minLength?: number;
  maxLength?: number;
  disabled?: boolean;
  rows?: number;
  expandable?: boolean;
  onKeyDown?: KeyboardEventHandler<HTMLTextAreaElement>;
};

function textAreaClasses({ error, expandable }: TextAreaProps) {
  return className(
    "w-full rounded-b-md border py-3 px-2 bg-white disabled:bg-slate-100 disabled:border-slate-200 disabled:text-slate-400",
    {
      "rounded-t-md": !expandable,
      "border-error": !!error,
      "border-slate-300": !error,
    },
  );
}

const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (props: TextAreaProps, ref) => {
    if (isControlled(props)) {
      return <ControlledTextArea {...props} ref={ref} />;
    }

    return <UncontrolledTextArea {...props} ref={ref} />;
  },
);
TextArea.displayName = "TextArea";

export default TextArea;

const UncontrolledTextArea = React.forwardRef<
  HTMLTextAreaElement,
  TextAreaProps
>((props: TextAreaProps, ref) => {
  const { onChange, onBlur, ...rest } = props;
  const [value, setValue] = useState(props.defaultValue ?? "");

  return (
    <ControlledTextArea
      ref={ref}
      value={value}
      onBlur={onBlur}
      onChange={(ev) => {
        onChange?.(ev);
        setValue(ev.target.value);
      }}
      {...rest}
    />
  );
});
UncontrolledTextArea.displayName = "UncontrolledTextArea";

function isControlled(props: TextAreaProps): props is ControlledTextAreaProps {
  return isPresent(props.onChange) && isPresent(props.value);
}

type ControlledTextAreaProps = Omit<
  TextAreaProps,
  "defaultValue" | "onChange" | "value"
> & {
  onChange: ChangeEventHandler<HTMLTextAreaElement>;
  value: string;
};

const ControlledTextArea = React.forwardRef<
  HTMLTextAreaElement,
  ControlledTextAreaProps
>((props: ControlledTextAreaProps, ref) => {
  const {
    required = true,
    label,
    id,
    name,
    autocomplete = "off",
    error,
    tabIndex = undefined,
    onChange,
    maxLength,
    disabled,
    value,
    rows,
    expandable = false,
    onBlur,
  } = props;
  const [length, setLength] = useState(value?.length);
  const exceededMaxLength = length > (maxLength || Infinity);

  function getError() {
    if (exceededMaxLength) {
      return "För lång";
    }

    return error;
  }

  const onChangeHandler: ChangeEventHandler<HTMLTextAreaElement> = (ev) => {
    setLength(ev.target.value.length);
    onChange?.(ev);
  };

  return (
    <div className="block w-full space-y-2">
      <div className="flex flex-row items-center justify-between">
        <Label id={id} required={required} label={label} disabled={disabled} />
        {maxLength && (
          <span className="ml-auto text-sm">
            {length}/{maxLength}
          </span>
        )}
      </div>
      <div>
        {expandable && <Toolbar {...props} />}
        <textarea
          ref={ref}
          rows={rows}
          value={value}
          onInput={(e) =>
            (e.target as HTMLTextAreaElement).setCustomValidity("")
          }
          disabled={disabled}
          onBlur={onBlur}
          onChange={onChangeHandler}
          tabIndex={tabIndex}
          id={id}
          required={required}
          name={name ?? id}
          onKeyDown={props.onKeyDown}
          autoComplete={autocomplete}
          className={textAreaClasses({
            ...props,
            error: getError(),
          })}
        />
      </div>
      <FieldError error={getError()} />
    </div>
  );
});
ControlledTextArea.displayName = "ControlledTextArea";

function Toolbar(props: TextAreaProps) {
  const { expandable: _expandable, label, rows: _rows, ...rest } = props;
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="flex flex-row justify-end rounded-t-md border-x border-t border-slate-300">
      <IconButton icon="expand" onClick={() => setExpanded(true)} />
      <Modal
        show={expanded}
        title={label ?? ""}
        onCancel={() => setExpanded(false)}
        footer={
          <ModalFooter sticky>
            <Button onClick={() => setExpanded(false)}>Klar</Button>
          </ModalFooter>
        }
      >
        <TextArea {...rest} rows={20} />
      </Modal>
    </div>
  );
}

TextArea.defaultProps = {
  defaultValue: "",
  tabIndex: undefined,
};
